import React, { useEffect, useState } from 'react';
import moment from 'moment';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Base64 } from 'js-base64';
import { Spin } from 'antd';

import { CondoLocationsEnum, CondoRequestTypeEnum, ILuxuryHome } from '@share/common-types';
import { condoDetailsActions, getCondoDetails } from '@share/store/slices';
import { ROOMS_UNAVAILABLE_ERROR } from '@constants';
import { useQueryParams } from '@utils';
import { RootState } from '@share/utils';

import { LuxuryHomesDetailsActionPriceDetails } from '../action-price-details';
import { LuxuryHomesDetailsSeasonalPricing } from '../actions-seasonal-pricing';
import { HomesDatePicker } from '../date-picker';

import './style.scss';

interface IProps {
  home: ILuxuryHome;
}

export const LuxuryHomesDetailsActions: React.FC<IProps> = ({ home }) => {

  const dispatch = useDispatch();
  
  const { checkIn, checkOut } = useQueryParams();

  const [startDate, setStartDate] = useState<Date | null | undefined>(checkIn ? new Date(checkIn) : undefined);
  const [endDate, setEndDate] = useState<Date | null | undefined>(checkOut ? new Date(checkOut) : undefined);
  const [editDates, setEditDates] = useState<boolean>(checkIn && checkOut ? false : true);

  const { condo, error, loading } = useSelector((state: RootState) => state.  condoDetailsStore);

  const condoDetails = condo?.condoDetails;

  const { roomId } = useParams<{ roomId: string }>();

  const decodedRoomId = roomId ? Base64.decode(roomId) : undefined;

  useEffect(() => {
    dispatch(condoDetailsActions.setLoading(false));
    dispatch(condoDetailsActions.setError(''));
    dispatch(condoDetailsActions.setCondoDetails(undefined));

    setEditDates(false);
    setStartDate(undefined);
    setEndDate(undefined);
  }, []);

  useEffect(() => {
    if (home) {
      const startDate = checkIn ? moment(checkIn, 'YYYY-MM-DD').toDate() : undefined;
      const endDate = checkOut ? moment(checkOut, 'YYYY-MM-DD').toDate() : undefined;
      const editDates = checkIn && checkOut ? false : true;
  
      setEditDates(editDates);
      setStartDate(startDate);
      setEndDate(endDate);
  
      if (startDate && endDate) {
        handleGetDates(startDate, endDate);
      }
    }
  }, [home]);

  const setError = (error: string) => {
    dispatch(condoDetailsActions.setError(error));
  };

  const handleGetDates = (startDate: Date | null | undefined, endDate: Date | null | undefined) => {
    dispatch(getCondoDetails(home?.id, undefined, false, {
      requestType: CondoRequestTypeEnum.Strict,
      adultsCount: 2,
      childrenCount: 0,
      bedroomsCount: 0,
      checkIn: startDate ? format(startDate, 'yyyy-MM-dd') : '',
      checkOut: endDate ? format(endDate, 'yyyy-MM-dd') : '',
      includeStudio: true,
      location: {
        geoLocation: {
          latitude  : home?.location?.location?.lat,
          longitude: home?.location?.location?.lng,
        },
        id: home?.location?.id?.toString(),
        locationType: CondoLocationsEnum.GenericCondo,
        name: home?.location?.name,
      }
    }, undefined, undefined, true, (details) => {
      const homeUnit = details.units.find((unit: any) => unit.roomId === decodedRoomId);

      if (homeUnit) {
        setEditDates(false);
      } else {
        setEditDates(true);
        dispatch(condoDetailsActions.setCondoDetails(undefined));
        dispatch(condoDetailsActions.setError(ROOMS_UNAVAILABLE_ERROR));
      }
    }));
  };

  return (  
    <aside className="booking-sidebar">
      <LuxuryHomesDetailsSeasonalPricing  />

      <div className="pricing-table">
        <h3>Book Your Vacation</h3>

        <HomesDatePicker 
          startDate={startDate}
          endDate={endDate}
          error={error}

          editDates={!condoDetails || editDates}

          setStartDate={setStartDate}
          setEndDate={setEndDate}
          setError={setError}
          setEditDates={setEditDates}

          leftAlign
        />

        {(!condoDetails || editDates) &&
          <Spin spinning={loading}>
            <button 
              className="book-vacation"
              disabled={loading || !startDate || !endDate}
              onClick={() => handleGetDates(startDate, endDate)}
            >
              SELECT DATES
            </button>
          </Spin>}  

        <LuxuryHomesDetailsActionPriceDetails 
          home={home}
          startDate={startDate}
          endDate={endDate}
          editDates={editDates}
        />
      </div>

      {!!home?.facilities?.length &&
        <div className="services">
          <h3>Services</h3>

          <ul>
            {home?.facilities.map((s: any) => (
              <li key={s.name}>{s.name} {s.proximity && <span className="proximity"> - {s.proximity}</span>}</li>))}
          </ul>
        </div>}
    </aside>
  );
};
